/*
@see https://justinternet.atlassian.net/browse/S20R-246
 */

/**
 * FUNCTIONALLY:
 *
 * Sorting order (sorting groups):
 * 1.) Contacts VIPs
 * 2.) Channel VIPs
 * 3.) Non-VIPs
 *
 * **!!! In archive the sorting will always be newest to oldest, it will disregard vip status and such**
 *
 * For all applies:
 * -) Smart inbox enabled:
 * 1.) Longest waiting consumer on the bottom
 * -) Smart inbox disabled:
 * 1.) Latest message on top, like WhatsApp.
 *
 * How to decide who's the longest waiting consumer (and should have the most attention, so should be on top):
 * 1.) Consumer sends message, so a new inbound message will come in (ex. time 13:00). This message will impact the sorting. Will apparently show on the bottom of the list, because it's likely that there are longer waiting conversations.
 * 2.) Consumer sends second message (ex. time 13:05), this message (and following messages) will NOT impact the sorting. Conversation will stay at the same position. We should NOT sort based on "lastInboundMessageTime".
 * 3.) Agent clicks conversation. This will NOT impact the sorting. Sorting should not be based on conversation/messages (un)read status.
 * 4.) Agent sends back a message, this WILL impact the order. The conversation will be moved to bottom, BELOW the "shortest" waiting customer.
 *
 * Decide sorting (technically it's always a comparison between two chats, as long as there are chats to compare):
 * 1. ) Contact priority (yes/no). Differences between chat A and chat B in contact priority?
 * 1a.) Yes. Chat with the contact priority above the chat without contact priority.
 * 2b.) No. Do not change order of chat A and chat B.
 *
 * 2.  ) Channel priority (yes/no). Differences between chat A and chat B in channel priority?
 * 2a. ) Yes. Does both chats have a contact priority (yes/no)?
 * 2a1.)   Yes. Contact priority WITHOUT channel priority above contact priority WITH channel priority
 * 2a2.)   No. Chat with the channel priority above the chat without channel priority.
 * 2b. ) No. Do not change order of chat A and chat B.
 *
 * 3.    ) Non-priority (also applies to contact priority and channel priority chats if both chat A and chat B are equal).
 *           Does the chat actually have an inbound message?
 * 3a.   ) Yes. Differences between chat A and chat B in last message direction (inbound/outbound)?
 * 3a1.  )   Yes. Chat A last message direction is OUTbound AND chat B last message direction is INbound?
 * 3a1a. )     Yes. Chat with last message direction INbound above chat with last message direction OUTbound
 * 3a1b. )     No. Do not change order, because they're already in correct order.
 * 3a2.  )   No. Is datetime of the FIRST message of the LAST INbound message GROUP of chat A equal to chat B?
 * 3a2a. )     Yes. Order by the chat identifier (primary key)
 * 3a2b. )     No. Is datetime of chat A higher than chat B?
 * 3a2a1.)       Yes. Chat A waits LESS longer, so chat A below chat B.
 * 3a2a2.)       No. Do not change order of chat A and chat B.
 * 3b.   ) No. Chat is newly created or only have outbound messages and appears below the other chats.
 */

import formattedChatsListChatInterface from "@/interfaces/inbox/formattedChatsListChat.interface";
// @ts-ignore
import i18n from "@/libs/i18n";
// @ts-ignore
import useAppConfig from "@/composables/useAppConfig";
// @ts-ignore
import { diff } from "@/utils/luxon";
import { Ref } from "@vue/composition-api";

export default function useChatsListSorting(
    formattedChats: formattedChatsListChatInterface[],
    smartInboxAvailable: Ref,
    activeFilter: Ref
) {
    const { smartInbox } = useAppConfig();

    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
     *
     * > 0    sort A after B - sort nextChat after firstChat
     * < 0    sort A before B - sort nextChat before firstChat
     * === 0  keep original order of A and B
     */

    /**
     *
     * @param nextChat if you have two chats, id 1 and 2, then id 2 will be this nextChat.
     * @param firstChat if you have two chats, id 1 and 2, then id 1 will be this firstChat.
     */
    const sortFunction = (nextChat: formattedChatsListChatInterface, firstChat: formattedChatsListChatInterface) => {
        if (activeFilter && activeFilter.value?.status?.name === "archive") {
            return diff(
                firstChat.lastConversation?.lastEntry?.time || "1970-01-01T00:00:00.000Z",
                nextChat.lastConversation?.lastEntry?.time || "1970-01-01T00:00:00.000Z"
            ).milliseconds;
        }

        // CONTACT PRIORITY
        if (firstChat.contact?.isHighPriority > nextChat.contact?.isHighPriority) {
            return 1;
        }

        if (firstChat.contact?.isHighPriority < nextChat.contact?.isHighPriority) {
            return -1;
        }

        // CHANNEL PRIORITY: we'll be here when:
        // - contact1 and contact2 high prio is both 0.
        // - contact1 and contact2 high prio is both 1.
        const firstChannelPriority = parseInt(
            firstChat.channel?.settings?.find((setting) => setting.name === "priority")?.value ?? "0"
        );
        const nextChannelPriority = parseInt(
            nextChat.channel?.settings?.find((setting) => setting.name === "priority")?.value ?? "0"
        );

        // Contact both prio? Then channel prio's should even be below the non-channel prio, so:
        // - contact.prio WITHOUT channel.prio should occur above contact.prio WITH channel.pio
        if (
            nextChat.contact?.isHighPriority &&
            firstChat.contact?.isHighPriority &&
            firstChannelPriority > nextChannelPriority
        ) {
            return -1;
        }

        if (firstChannelPriority > nextChannelPriority) {
            return 1;
        }

        if (firstChannelPriority < nextChannelPriority) {
            return -1;
        }

        //NON-PRIORITIES (will also applied to contact priority and channel priority groups): we'll be here when:
        // - contact prio AND channel prio are equal of both firstChat and nextChat, no matter if it's true or false, as long as they are equal.

        if (!smartInboxAvailable || parseInt(smartInbox.value) === 0) {
            // Latest incoming message on top, independently of the directions.
            const dateDiffMilliseconds = diff(
                firstChat.lastConversation?.lastEntry?.time || "1970-01-01T00:00:00.000Z",
                nextChat.lastConversation?.lastEntry?.time || "1970-01-01T00:00:00.000Z"
            ).milliseconds;

            if (dateDiffMilliseconds < 0) {
                return -1;
            }

            if (dateDiffMilliseconds > 0) {
                return 1;
            }
        } else if (parseInt(smartInbox.value) === 1) {
            if (
                firstChat.lastConversation?.lastEntry?.direction === "out" &&
                nextChat.lastConversation?.lastEntry?.direction === "in"
            ) {
                return -1;
            }

            if (
                firstChat.lastConversation?.lastEntry?.direction === "in" &&
                nextChat.lastConversation?.lastEntry?.direction === "out"
            ) {
                return 1;
            }

            // Are the directions equal of both firstChat and nextChat, then we'll sort by datetime.

            // If we do not HAVE any inbound message, chat should be below the ones that does have any inbound message.
            if (!firstChat.lastInboundMessageTime && nextChat.lastInboundMessageTime) {
                //if (!firstChat.firstMessageTimeLastInboundEntryGroup && nextChat.firstMessageTimeLastInboundEntryGroup) {
                return -1;
            }

            if (firstChat.lastInboundMessageTime && !nextChat.lastInboundMessageTime) {
                //if (firstChat.firstMessageTimeLastInboundEntryGroup && !nextChat.firstMessageTimeLastInboundEntryGroup) {
                return 1;
            }

            let dateDiffMilliseconds;
            // Agent has responded (direction out), also in this case we're sorting on date whereby the last active chat will below the lesser active chat.
            if (
                nextChat.lastConversation?.lastEntry?.direction === "out" &&
                firstChat.lastConversation?.lastEntry?.direction === "out"
            ) {
                dateDiffMilliseconds = diff(
                    firstChat.lastConversation.lastEntry.time,
                    nextChat.lastConversation.lastEntry.time
                ).milliseconds;
                if (dateDiffMilliseconds > 0) {
                    return -1;
                }
            }

            // We'll come here when directions of both chat A and chat B are IN.
            dateDiffMilliseconds = diff(firstChat.lastInboundMessageTime, nextChat.lastInboundMessageTime).milliseconds;
            /*dateDiffMilliseconds = diff(
                firstChat.firstMessageTimeLastInboundEntryGroup,
                nextChat.firstMessageTimeLastInboundEntryGroup
            ).milliseconds;*/
            // When both chats have no inbound messages (whats unlikely at this position), the diff will also be undefined.
            if (typeof dateDiffMilliseconds === "undefined") {
                return 1;
            }

            // In this case, the nextChat is waiting longer, so need more attention, so above the firstChat.
            if (dateDiffMilliseconds > 0) {
                return -1;
            }

            if (dateDiffMilliseconds < 0) {
                return 1;
            }
        }

        return 0;
    };

    return formattedChats.sort(sortFunction);
}
